import { Loader, Navigation } from 'lucide-react';
import { type FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMap } from 'react-map-gl/mapbox';

import type { TooltipState } from '@/types';

import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import usePinnedItem from '@/hooks/use-pinned-item';
import {
  DEFAULT_LOCATION_LABEL,
  GEO_ERRORS,
  MAX_ZOOM_IN,
  RHB_NETWORK_DEFAULT_CENTER,
  RHB_NETWORK_SEARCH_BOUNDS,
  ZOOM_11,
} from '@/utils/constants/map';
import { isPointWithinBounds } from '@/utils/helpers/train-simulation/is-point-within-bounds';

const RecenterButton: FC = () => {
  const { t } = useTranslation();
  const { current: map } = useMap();

  const suppressHoverRef = useRef(false);
  const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [geoPermission, setGeoPermission] = useState<PermissionState>('prompt');
  const [tooltip, setTooltip] = useState<TooltipState>({
    mode: 'idle',
    message: t(DEFAULT_LOCATION_LABEL),
  });

  const { pinnedItemId, pinnedItemType } = usePinnedItem();
  const isDisabled = Boolean(pinnedItemId) && pinnedItemType === 'route';

  const flyToDefault = useCallback(() => {
    map?.flyTo({
      center: RHB_NETWORK_DEFAULT_CENTER,
      zoom: ZOOM_11,
      duration: 1500,
      essential: true,
    });
  }, [map]);

  const setTooltipState = useCallback((mode: TooltipState['mode'], message: string) => {
    if (resetTimerRef.current) {
      clearTimeout(resetTimerRef.current);
    }

    setTooltip({ mode, message });
  }, []);

  const closeTooltip = useCallback(() => {
    suppressHoverRef.current = true;

    setTooltip((prev) => ({ ...prev, mode: 'idle' }));

    if (resetTimerRef.current) {
      clearTimeout(resetTimerRef.current);
    }

    resetTimerRef.current = setTimeout(() => {
      setTooltip({ mode: 'idle', message: t(DEFAULT_LOCATION_LABEL) });
    }, 300);
  }, [t]);

  const handleGeoError = useCallback(
    (message: string) => {
      flyToDefault();
      setIsLoading(false);
      setTooltipState('error', t(message));
    },
    [flyToDefault, setTooltipState]
  );

  const handleMouseEnter = () => {
    if (tooltip.mode === 'idle' && !isLoading && !suppressHoverRef.current) {
      setTooltipState('hover', t(DEFAULT_LOCATION_LABEL));
    }
  };

  const handleMouseLeave = () => {
    suppressHoverRef.current = false;

    if (tooltip.mode === 'hover') {
      setTooltipState('idle', t(DEFAULT_LOCATION_LABEL));
    }
  };

  const handleRecenter = useCallback(() => {
    if (!map) {
      return;
    }

    setTooltipState('idle', t(DEFAULT_LOCATION_LABEL));

    if (!navigator.geolocation) {
      return handleGeoError(GEO_ERRORS.NOT_SUPPORTED);
    }

    if (geoPermission === 'denied') {
      return handleGeoError(GEO_ERRORS.PERMISSION_DENIED);
    }

    setIsLoading(true);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;

        if (!isPointWithinBounds([longitude, latitude], RHB_NETWORK_SEARCH_BOUNDS)) {
          return handleGeoError(GEO_ERRORS.OUTSIDE_NETWORK);
        }

        map.flyTo({
          center: [longitude, latitude],
          zoom: MAX_ZOOM_IN,
          duration: 1500,
          essential: true,
        });

        setIsLoading(false);
        closeTooltip();
      },
      (error) => {
        let msg = GEO_ERRORS.UNKNOWN;

        if (error.code === error.PERMISSION_DENIED) {
          msg = GEO_ERRORS.PERMISSION_DENIED;
        } else if (error.code === error.POSITION_UNAVAILABLE) {
          msg = GEO_ERRORS.POSITION_UNAVAILABLE;
        }

        handleGeoError(msg);
      },
      {
        enableHighAccuracy: true,
        timeout: Infinity,
        maximumAge: 0,
      }
    );
  }, [map, geoPermission, handleGeoError, closeTooltip, setTooltipState]);

  useEffect(() => {
    if (!navigator.permissions) {
      return;
    }

    const updatePermission = (status: PermissionStatus) => {
      setGeoPermission(status.state);
    };

    navigator.permissions.query({ name: 'geolocation' }).then((status) => {
      updatePermission(status);
      status.onchange = () => updatePermission(status);
    });
  }, []);

  useEffect(() => {
    return () => {
      if (resetTimerRef.current) {
        clearTimeout(resetTimerRef.current);
      }
    };
  }, []);

  return (
    <Tooltip open={tooltip.mode !== 'idle'}>
      <TooltipTrigger asChild>
        <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} className="w-max">
          <Button
            className="bg-white size-10 flex justify-center items-center rounded-full shadow-sm p-0 transition-colors"
            aria-label="current-location"
            disabled={isDisabled}
            onClick={handleRecenter}
          >
            {isLoading ? (
              <Loader className="size-5 animate-spin" />
            ) : (
              <Navigation className="size-5" fill="black" />
            )}
          </Button>
        </div>
      </TooltipTrigger>

      <TooltipContent
        side="right"
        onPointerDownOutside={closeTooltip}
        onEscapeKeyDown={closeTooltip}
        className="max-w-55"
      >
        <p>{tooltip.message}</p>
      </TooltipContent>
    </Tooltip>
  );
};

export default RecenterButton;
